/**
 * File: CaseWorkflowDashboard.jsx
 * Path: /src/components/solutions/CaseWorkflowDashboard.jsx
 * Description: Standalone dashboard visualization for case workflows and analytics
 * Key Features: Visual workflow management, case analytics, and team productivity metrics
 */

import React from 'react';
// Removed unused motion import: import { motion } from 'framer-motion';

const CaseWorkflowDashboard = () => {
  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-primary-100 to-primary-50 p-1">
        <div className="bg-white rounded-lg p-6 relative">
          {/* Background Pattern (Optional visual element) */}
          <div className="absolute inset-0 opacity-5 pointer-events-none"> {/* Added pointer-events-none */}
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="document-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#document-pattern)" />
            </svg>
          </div>

          {/* Dashboard container */}
          {/* Removed min-h-[440px] and flex center as content now defines height */}
          <div className="w-full relative z-10">
            {/* Main Content Wrapper */}
            <div className="flex flex-col h-full">
              {/* Header Row */}
              <div className="flex items-center justify-between mb-4">
                {/* Left Side: Title */}
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  {/* *** MOVED THIS SPAN INSIDE *** */}
                  <span className="font-semibold text-dark-600">Case Workflow Management</span>
                </div>
                {/* Right Side: Badges */}
                {/* *** MOVED THIS DIV INSIDE *** */}
                <div className="flex space-x-3">
                  <div className="py-1 px-3 text-xs bg-blue-50 text-blue-600 rounded-full">28 Active Cases</div>
                  <div className="py-1 px-3 text-xs bg-green-50 text-green-600 rounded-full">8 Team Members</div>
                </div>
              </div> {/* End Header Row */}

              {/* Workflow Dashboard Grid Area */}
              {/* *** MOVED THIS GRID INSIDE *** */}
              <div className="flex-grow grid grid-cols-12 gap-4">

                {/* Left side - Workflow Stages */}
                <div className="col-span-8 border border-dark-100 rounded-lg p-4 text-sm text-dark-700">
                  <div className="text-base font-bold mb-3">Case Pipeline</div>
                  <div className="grid grid-cols-4 gap-3">

                    {/* Workflow column - Intake */}
                    <div className="border border-dark-100 rounded-lg p-2">
                      <div className="text-center py-1 mb-2 bg-blue-50 text-blue-600 rounded-md text-sm font-medium">
                        Intake
                      </div>
                      <div className="space-y-2">
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Pearson Consultation</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Added: May 2</span>
                            <span className="text-[10px] bg-blue-100 text-blue-600 px-1 rounded">New</span>
                          </div>
                        </div>
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Garcia Family Trust</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Added: May 1</span>
                            <span className="text-[10px] bg-blue-100 text-blue-600 px-1 rounded">New</span>
                          </div>
                        </div>
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Thompson Referral</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Added: Apr 28</span>
                            <span className="text-[10px] bg-blue-100 text-blue-600 px-1 rounded">New</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workflow column - Investigation */}
                    <div className="border border-dark-100 rounded-lg p-2">
                      <div className="text-center py-1 mb-2 bg-purple-50 text-purple-600 rounded-md text-sm font-medium">
                        Investigation
                      </div>
                      <div className="space-y-2">
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Rodriguez Contract</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Updated: May 3</span>
                            <span className="text-[10px] bg-purple-100 text-purple-600 px-1 rounded">Docs</span>
                          </div>
                        </div>
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Morrison Property</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Updated: May 2</span>
                            <span className="text-[10px] bg-purple-100 text-purple-600 px-1 rounded">Docs</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workflow column - Active */}
                    <div className="border border-dark-100 rounded-lg p-2">
                      <div className="text-center py-1 mb-2 bg-green-50 text-green-600 rounded-md text-sm font-medium">
                        Active
                      </div>
                      <div className="space-y-2">
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Smith v. Acme Corp</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Status: Discovery</span>
                            <span className="text-[10px] bg-yellow-100 text-yellow-600 px-1 rounded">Urgent</span>
                          </div>
                        </div>
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Johnson IP Dispute</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Status: Motions</span>
                            <span className="text-[10px] bg-green-100 text-green-600 px-1 rounded">On track</span>
                          </div>
                        </div>
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Martinez Estate</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Status: Drafting</span>
                            <span className="text-[10px] bg-green-100 text-green-600 px-1 rounded">On track</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Workflow column - Closing */}
                    <div className="border border-dark-100 rounded-lg p-2">
                      <div className="text-center py-1 mb-2 bg-indigo-50 text-indigo-600 rounded-md text-sm font-medium">
                        Closing
                      </div>
                      <div className="space-y-2">
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Henderson Settlement</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Closing: May 7</span>
                            <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1 rounded">Final</span>
                          </div>
                        </div>
                        <div className="bg-white shadow-sm border border-dark-100 p-2 rounded-md hover:shadow-md transition-shadow">
                          <div className="text-xs font-medium mb-1">Park LLC Formation</div>
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] text-dark-400">Closing: May 10</span>
                            <span className="text-[10px] bg-indigo-100 text-indigo-600 px-1 rounded">Final</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> {/* End Grid for Columns */}
                </div> {/* End Left Side Column */}

                {/* Right side - Analytics and Metrics */}
                <div className="col-span-4 flex flex-col space-y-4">

                  {/* Case Distribution */}
                  <div className="border border-dark-100 rounded-lg p-3 flex-1">
                    <div className="text-dark-700 font-medium mb-2 text-sm">Case Distribution</div>
                    <div className="flex items-end h-36 space-x-2 mt-4">
                      <div className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-blue-500 h-1/3 rounded-t"></div>
                        <div className="mt-1 text-xs text-dark-500">Intake</div>
                        <div className="text-xs font-medium text-dark-700">8</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-purple-500 h-1/4 rounded-t"></div>
                        <div className="mt-1 text-xs text-dark-500">Investigation</div>
                        <div className="text-xs font-medium text-dark-700">6</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-green-500 h-1/2 rounded-t"></div>
                        <div className="mt-1 text-xs text-dark-500">Active</div>
                        <div className="text-xs font-medium text-dark-700">12</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-indigo-500 h-1/6 rounded-t"></div>
                        <div className="mt-1 text-xs text-dark-500">Closing</div>
                        <div className="text-xs font-medium text-dark-700">4</div>
                      </div>
                    </div>
                  </div>

                  {/* Team Workload */}
                  <div className="border border-dark-100 rounded-lg p-3 flex-1">
                    <div className="text-dark-700 font-medium mb-2 text-sm">Team Workload</div>
                    {/* *** MERGED WORKLOAD ITEMS HERE *** */}
                    <div className="space-y-2 mt-1">
                        {/* Item 1 */}
                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <span>Sarah Peterson</span>
                                <span>4 cases</span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full">
                                <div className="h-full bg-blue-500 rounded-full" style={{ width: '40%' }}></div>
                            </div>
                        </div>
                         {/* Item 2 */}
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Jane Smith</span>
                            <span>8 cases</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: '80%' }}></div>
                          </div>
                        </div>
                        {/* Item 3 */}
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Michael Rodriguez</span>
                            <span>6 cases</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: '60%' }}></div>
                          </div>
                        </div>
                        {/* Item 4 */}
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span>Kevin Thompson</span>
                            <span>10 cases</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: '100%' }}></div>
                          </div>
                        </div>
                    </div>
                  </div>

                  {/* Upcoming Deadlines */}
                  <div className="border border-dark-100 rounded-lg p-3 flex-1">
                    <div className="text-dark-700 font-medium mb-2 text-sm">Upcoming Deadlines</div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs py-1 border-b border-gray-100">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                          <span>Expert Disclosure (Smith)</span>
                        </div>
                        <span className="font-medium">May 12</span>
                      </div>
                      <div className="flex justify-between items-center text-xs py-1 border-b border-gray-100">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                          <span>Deposition (Johnson)</span>
                        </div>
                        <span className="font-medium">May 15</span>
                      </div>
                      <div className="flex justify-between items-center text-xs py-1 border-b border-gray-100">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          <span>Filing Deadline (Park)</span>
                        </div>
                        <span className="font-medium">May 18</span>
                      </div>
                    </div>
                  </div>
                </div> {/* End Right Side Column */}
              </div> {/* End Workflow Dashboard Grid Area */}
            </div> {/* End Main Content Wrapper */}
          </div> {/* End Dashboard container */}
        </div> {/* End bg-white */}
      </div> {/* End bg-gradient */}
    </div> /* End Relative Root Div */
  );
};

export default CaseWorkflowDashboard; // <<< CORRECT PLACEMENT OF EXPORT